/* eslint-disable react-hooks/rules-of-hooks */
import { RoleName } from '@/api/entities/user-role-type';
import Loader from '@/components/Loading/Loader';
import Modal from '@/components/Modal';
import { NotFound } from '@/components/Message/NotFound';
import { EntityTag } from '@/components/Tag/EntityTag';
import {
  useCreateUserEntityRole,
  useDeleteManyUserEntityRoles,
  useDeleteUserEntityRole,
} from '@/hooks/user-entity-role.hooks';
import { useGetUserRoleTypeByName } from '@/hooks/user-role-type.hooks';
import { TUserEntityRoleSchema } from '@/schemas/user-entity-role/user-entity-role.schema';
import { showModal } from '@/utils/modal.utils';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { SingleIconButton } from '@/components/Custom/Button/SingleIconButton';
import { PlusIcon } from '@/components/Custom/Icons/PlusIcon';
import { TFunction } from 'i18next';

interface UserRolesManagementProps<TSchema extends FieldValues> {
  t: TFunction<undefined>;
  userId: string;
  entityField?: string;
  roleName: RoleName;
  roleNameLabel: string;
  assignEntityText?: string;
  mainContentNotFoundText?: string;
  modalTitle?: string;
  modalDataNotFoundText?: string;
  useGetUnassignedEntities?: (
    foreignKeyId: string,
    roleTypeId?: string,
  ) => {
    data?: { data: TSchema[] };
    isLoading: boolean;
    refetch?: () => void;
  };
  useGetAssignedEntities?: (
    foreignKeyId: string,
    roleTypeId?: string,
  ) => {
    data?: { data: TSchema[] };
    isLoading: boolean;
    refetch?: () => void;
  };
}

export const UserRolesManagement = <TSchema extends FieldValues>({
  t,
  userId,
  entityField,
  roleName,
  roleNameLabel,
  assignEntityText,
  mainContentNotFoundText,
  modalTitle,
  modalDataNotFoundText,
  useGetUnassignedEntities,
  useGetAssignedEntities,
}: UserRolesManagementProps<TSchema>) => {
  const [selectedRoleName, setSelectedRoleName] = useState<RoleName>();
  const [selectedEntityIDs, setSelectedEntityIDs] = useState<string[]>([]);
  const [deletingEntityIDs, setDeletingEntityIDs] = useState<string[]>([]);

  const { data: userRoleType, isLoading: isLoadingUserRoleType } =
    useGetUserRoleTypeByName(selectedRoleName ?? roleName);

  const {
    data: unassignedEntities,
    isLoading: isLoadingUnassignedEntities,
    refetch: refetchUnassignedEntities,
  } = useGetUnassignedEntities
    ? useGetUnassignedEntities(userId, userRoleType?.data?.id)
    : { data: undefined, isLoading: false, refetch: undefined };

  const {
    data: assignedEntities,
    isLoading: isLoadingAssignedEntities,
    refetch: refetchAssignedEntities,
  } = useGetAssignedEntities
    ? useGetAssignedEntities(userId, userRoleType?.data?.id)
    : { data: undefined, isLoading: false, refetch: undefined };

  useEffect(() => {
    if (
      !isLoadingAssignedEntities &&
      assignedEntities &&
      (roleName !== RoleName.ADMIN && roleName !== RoleName.MODERATOR
        ? assignedEntities.data && assignedEntities.data.length > 0
        : assignedEntities.data)
    ) {
      setSelectedRoleName(roleName);
    }
  }, [isLoadingAssignedEntities, assignedEntities, roleName]);

  // Clear deletingEntityIDs for entities that are confirmed deleted from server
  useEffect(() => {
    if (
      !isLoadingAssignedEntities &&
      assignedEntities &&
      deletingEntityIDs.length > 0
    ) {
      const currentEntityIds =
        assignedEntities.data?.map((entity: TSchema) => `${entity.id}`) || [];
      const stillDeleting = deletingEntityIDs.filter((id) =>
        currentEntityIds.includes(id),
      );

      if (stillDeleting.length !== deletingEntityIDs.length) {
        setDeletingEntityIDs(stillDeleting);
      }
    }
  }, [isLoadingAssignedEntities, assignedEntities, deletingEntityIDs]);

  const {
    mutate: mutateCreateUserEntityRole,
    isPending: isPendingCreateUserEntityRole,
  } = useCreateUserEntityRole({
    handleSuccess: () => {
      setSelectedEntityIDs([]);
      // Refetch data to ensure UI is updated
      refetchUnassignedEntities?.();
      refetchAssignedEntities?.();
    },
  });

  const {
    mutate: mutateDeletedUserEntityRole,
    isPending: isPendingDeletedUserEntityRole,
  } = useDeleteUserEntityRole({
    handleSuccess: () => {
      setSelectedEntityIDs([]);
      // Refetch data to ensure UI is updated
      refetchUnassignedEntities?.();
      refetchAssignedEntities?.();
    },
  });

  const {
    mutate: mutateDeleteManyUserEntityRoles,
    isPending: isPendingDeleteManyUserEntityRoles,
  } = useDeleteManyUserEntityRoles({
    handleSuccess: () => {
      setSelectedEntityIDs([]);
      // Refetch data to ensure UI is updated
      refetchUnassignedEntities?.();
      refetchAssignedEntities?.();
    },
  });

  const handleDeleteUserEntityRole = (
    userEntityRole: TUserEntityRoleSchema,
    entityId?: string,
  ) => {
    // Optimistically remove from UI
    if (entityId) {
      setDeletingEntityIDs((prev) => [...prev, entityId]);
    }
    mutateDeletedUserEntityRole(userEntityRole);
  };

  const handleAddUserEntityRole = () => {
    if (roleName === RoleName.ADMIN || roleName === RoleName.MODERATOR) {
      mutateCreateUserEntityRole({
        userId: userId,
        roleTypeId: userRoleType?.data?.id,
      });

      return;
    }

    if (selectedEntityIDs.length > 0) {
      for (const entityId of selectedEntityIDs) {
        mutateCreateUserEntityRole({
          [`${entityField}`]: entityId,
          userId: userId,
          roleTypeId: userRoleType?.data?.id,
        });
      }
    }
  };

  const handleDeleteRole = () => {
    // Unselect role name
    setSelectedRoleName(undefined);

    if (
      (roleName === RoleName.ADMIN || roleName === RoleName.MODERATOR) &&
      assignedEntities &&
      assignedEntities.data
    ) {
      const { entityRoles } = assignedEntities.data as unknown as TSchema;

      const entityRole = entityRoles.find(
        (entity: TUserEntityRoleSchema) =>
          entity?.roleTypeId === userRoleType?.data?.id,
      );
      handleDeleteUserEntityRole(entityRole);
      return;
    }

    if (
      assignedEntities &&
      assignedEntities.data &&
      assignedEntities.data.length > 0
    ) {
      // Optimistically remove all entities from UI
      const entityIds = assignedEntities.data.map(
        (item: TSchema) => `${item.id}`,
      );
      setDeletingEntityIDs((prev) => [...prev, ...entityIds]);

      const entityRoles = assignedEntities.data.map(
        (item: TSchema) => item?.entityRoles[0],
      );
      mutateDeleteManyUserEntityRoles(entityRoles);
    }
  };

  const handleSelectRole = () => {
    if (
      selectedRoleName !== undefined &&
      (roleName === RoleName.ADMIN ||
        roleName === RoleName.MODERATOR ||
        (assignedEntities &&
          assignedEntities.data &&
          assignedEntities.data.length > 0))
    ) {
      showModal(`#confirm-delete-${roleName}-role`);
    } else {
      if (roleName === RoleName.ADMIN || roleName === RoleName.MODERATOR) {
        if (assignedEntities && assignedEntities.data) {
          handleDeleteRole();
          return;
        } else {
          handleAddUserEntityRole();
        }
      }

      setSelectedRoleName((prevState) =>
        prevState === roleName ? undefined : roleName,
      );
    }
  };

  const handleSelectEntity = (id?: string) => {
    if (id) {
      if (selectedEntityIDs.includes(id)) {
        const entityIDs = selectedEntityIDs.filter(
          (selectedId: string) => selectedId !== id,
        );
        setSelectedEntityIDs(entityIDs);
      } else {
        setSelectedEntityIDs((prev: string[]) => [...prev, id]);
      }
    }
  };

  const renderUnassignedEntities = () => {
    if (
      !isLoadingUnassignedEntities &&
      unassignedEntities &&
      unassignedEntities.data &&
      unassignedEntities.data.length > 0
    ) {
      return unassignedEntities.data.map((entity: TSchema) => {
        const { id, logo, name } = entity;

        return (
          <EntityTag
            key={id}
            logo={logo}
            text={name}
            selected={selectedEntityIDs.includes(`${id}`)}
            onSelectDisabled={isPendingCreateUserEntityRole}
            onSelect={() => handleSelectEntity(id)}
          />
        );
      });
    }
    return <></>;
  };

  const renderAssignedEntities = () => {
    if (
      !isLoadingAssignedEntities &&
      assignedEntities &&
      assignedEntities.data &&
      assignedEntities.data.length > 0
    ) {
      return assignedEntities.data
        .filter(
          (entity: TSchema) => !deletingEntityIDs.includes(`${entity.id}`),
        )
        .map((entity: TSchema) => {
          const { id, logo, name, entityRoles } = entity;

          return (
            <EntityTag
              key={id}
              logo={logo}
              text={name}
              onDeleteDisabled={
                isPendingDeletedUserEntityRole ||
                isPendingDeleteManyUserEntityRoles
              }
              onDelete={() =>
                handleDeleteUserEntityRole(entityRoles[0], `${id}`)
              }
            />
          );
        });
    }

    return <></>;
  };

  return (
    <>
      <div className="flex flex-col gap-3 p-4 bg-slate-50 rounded-lg">
        <div className="flex">
          <EntityTag
            containerClassName="w-[235px] border-slate-300 max-sm:w-full"
            displayLogo={false}
            text={roleNameLabel}
            selected={selectedRoleName === roleName}
            onSelect={handleSelectRole}
          />
        </div>
        <div
          className={twMerge(
            'hidden flex-col gap-3',
            selectedRoleName === roleName &&
              selectedRoleName !== RoleName.ADMIN &&
              selectedRoleName !== RoleName.MODERATOR &&
              'flex',
          )}
        >
          <div className="flex">
            <div className="flex flex-wrap items-center border border-dashed border-slate-300 px-4 py-2 rounded-sm gap-5 max-sm:pt-5 max-sm:pb-4 max-sm:gap-1.5">
              <div className="w-[320px] flex items-center max-sm:w-full">
                <p
                  className={twMerge(
                    '-mt-0.5 text-gray-700 font-medium text-sm leading-3',
                    (roleName === RoleName.BRANCH_MANAGER ||
                      roleName === RoleName.BRANCH_AGENT) &&
                      'mt-1.75',
                  )}
                >
                  {assignEntityText} :{' '}
                  <span className="text-red-FF2929 text-lg">*</span>
                </p>
              </div>
              <div className="flex justify-center items-center max-sm:w-full">
                <SingleIconButton
                  icon={PlusIcon}
                  dataModalToggle={`#${roleName}-list-modal`}
                  buttonClassName="flex justify-center items-center w-9 h-9 !bg-blue-2948FF rounded-full shadow-xl hover:cursor-pointer"
                  onClick={() => setSelectedEntityIDs([])}
                />
              </div>
            </div>
          </div>
          {isLoadingAssignedEntities ? (
            <Loader />
          ) : !assignedEntities || assignedEntities?.data.length === 0 ? (
            <div className="m-10 max-sm:my-5 max-sm:mx-5 max-[375px]:mx-3">
              <NotFound text={mainContentNotFoundText} />
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2.5 max-1.5xl:grid-cols-3 max-xl:grid-cols-2 max-md:grid-cols-1">
              {renderAssignedEntities()}
            </div>
          )}
        </div>
      </div>
      <Modal
        id={`${roleName}-list-modal`}
        bodyClassName="p-2.5 m-0 min-h-[150px]"
        title={modalTitle}
        showCancelBtn={Boolean(
          unassignedEntities &&
          unassignedEntities.data &&
          unassignedEntities.data.length > 0,
        )}
        showSecondBtn={true}
        secondBtnLabel={
          unassignedEntities &&
          unassignedEntities.data &&
          unassignedEntities.data.length > 0
            ? t('common:add')
            : t('common:ok')
        }
        size={
          !isLoadingUnassignedEntities && unassignedEntities?.data.length === 0
            ? 'small'
            : 'large'
        }
        onClickSecondBtn={handleAddUserEntityRole}
        disabledSubmitButton={isPendingCreateUserEntityRole}
      >
        {isPendingCreateUserEntityRole ? (
          <Loader />
        ) : isLoadingUnassignedEntities ? (
          <Loader />
        ) : !unassignedEntities || unassignedEntities?.data.length === 0 ? (
          <div className="w-full h-[150px] flex justify-center items-center">
            <NotFound text={modalDataNotFoundText} />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2.5 max-1.5xl:grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1">
            {renderUnassignedEntities()}
          </div>
        )}
      </Modal>
      <Modal
        id={`confirm-delete-${roleName}-role`}
        title={t('common:confirmation')}
        size="small"
        showSecondBtn
        secondBtnLabel={t('common:yes')}
        onClickSecondBtn={handleDeleteRole}
      >
        <p>{`${t('areYouSureToWantToDeleteThisRoleFromThisUser')} ?`}</p>
      </Modal>
      {(isLoadingUserRoleType ||
        isPendingCreateUserEntityRole ||
        isPendingDeletedUserEntityRole ||
        isPendingDeleteManyUserEntityRoles) && <Loader />}
    </>
  );
};
